import {
  getStorefrontCopyValue,
  normalizeStorefrontLocale,
  storefrontCopyForTenant,
} from "lib/storefrontCopy";

const DEFAULT_IMAGE = "/signature.jpg";
const DEFAULT_ACCENT = "#FF5C00";
const VBS_TAB_ICON_TENANTS = new Set(["vbsus", "mx"]);
const PUBLIC_URL = process.env.PUBLIC_URL || "";
const VBS_FAVICON = {
  ico: `${PUBLIC_URL}/vbs-favicon.ico`,
  png16: `${PUBLIC_URL}/vbs-favicon-16.png`,
  png32: `${PUBLIC_URL}/vbs-favicon-32.png`,
  png180: `${PUBLIC_URL}/vbs-favicon-180.png`,
  png512: `${PUBLIC_URL}/vbs-favicon-512.png`,
};
const VBS_PWA_ICONS = [
  {
    src: `${PUBLIC_URL}/pwa-icon-192.png`,
    sizes: "192x192",
    type: "image/png",
    purpose: "any",
  },
  {
    src: `${PUBLIC_URL}/pwa-icon-512.png`,
    sizes: "512x512",
    type: "image/png",
    purpose: "any",
  },
  {
    src: `${PUBLIC_URL}/pwa-maskable-512.png`,
    sizes: "512x512",
    type: "image/png",
    purpose: "maskable",
  },
];
const VBS_HOST_SUFFIXES = ["vbspecialties.com.mx", "vbspecialties.com"];

function cleanText(value) {
  return String(value || "").trim();
}

function resolveAssetUrl(value, fallback = "") {
  const raw = cleanText(value);
  if (!raw) return fallback;
  if (/^(https?:|data:|blob:)/i.test(raw)) return raw;
  return raw.startsWith("/") ? raw : `/${raw}`;
}

function initialsFor(value) {
  const words = cleanText(value)
    .split(/\s+/)
    .map((word) => word.replace(/[^a-z0-9]/gi, ""))
    .filter(Boolean);
  if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
  const compact = words[0] || "S";
  return compact.slice(0, 2).toUpperCase();
}

function generatedIconUrl(brandName, accent) {
  const initials = initialsFor(brandName);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128"><rect width="128" height="128" rx="28" fill="${accent}"/><text x="64" y="77" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="44" font-weight="800" fill="#fff">${initials}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

function resolveTenantKey(tenant) {
  return cleanText(tenant?.companyKey || tenant?.key).toLowerCase();
}

function resolveBrandName(tenant) {
  const key = resolveTenantKey(tenant);
  const host =
    typeof window !== "undefined" ? cleanText(window.location.hostname) : "";
  const locale =
    key === "mx"
      ? "es-MX"
      : normalizeStorefrontLocale(tenant?.defaultLocale) || "en-US";
  const copy = storefrontCopyForTenant(tenant, locale);

  return (
    cleanText(tenant?.name) ||
    cleanText(tenant?.companyName) ||
    getStorefrontCopyValue(copy, "common.brandFallback", "") ||
    cleanText(tenant?.companyKey || tenant?.key) ||
    host ||
    "Storefront"
  );
}

function isVbsTabTenant(tenant) {
  if (VBS_TAB_ICON_TENANTS.has(resolveTenantKey(tenant))) return true;
  if (typeof window === "undefined") return false;
  const host = cleanText(window.location.hostname).toLowerCase();
  return VBS_HOST_SUFFIXES.some(
    (suffix) => host === suffix || host.endsWith(`.${suffix}`),
  );
}

function resolveTabIconUrl(tenant, brandName, accent) {
  if (isVbsTabTenant(tenant)) {
    return VBS_FAVICON.png512;
  }
  return resolveAssetUrl(tenant?.brandLogoUrl) || generatedIconUrl(brandName, accent);
}

function setVbsIconLinks() {
  if (typeof document === "undefined") return;

  document.head
    .querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
    .forEach((link) => link.remove());

  const links = [
    { rel: "icon", href: VBS_FAVICON.ico },
    { rel: "shortcut icon", href: VBS_FAVICON.ico },
    { rel: "icon", type: "image/png", sizes: "32x32", href: VBS_FAVICON.png32 },
    { rel: "icon", type: "image/png", sizes: "16x16", href: VBS_FAVICON.png16 },
    { rel: "apple-touch-icon", sizes: "180x180", href: VBS_FAVICON.png180 },
  ];

  links.forEach(({ rel, type, sizes, href }) => {
    const link = document.createElement("link");
    link.setAttribute("rel", rel);
    link.setAttribute("href", absoluteUrl(href));
    if (type) link.setAttribute("type", type);
    if (sizes) link.setAttribute("sizes", sizes);
    document.head.appendChild(link);
  });
}

function absoluteUrl(value) {
  if (typeof window === "undefined") return value;
  try {
    return new URL(value, window.location.origin).toString();
  } catch {
    return value;
  }
}

function currentCanonicalUrl() {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}${window.location.pathname}`;
}

function setMeta(selector, attr, value) {
  if (typeof document === "undefined" || !value) return;
  const tag = document.head.querySelector(selector);
  if (tag) tag.setAttribute(attr, value);
}

function setMetaByName(name, content) {
  setMeta(`meta[name="${name}"]`, "content", content);
}

function setMetaByProperty(property, content) {
  setMeta(`meta[property="${property}"]`, "content", content);
}

function setLink(selector, href) {
  if (typeof document === "undefined" || !href) return;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("link");
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function setIconLinks(iconUrl, tenant) {
  if (typeof document === "undefined" || !iconUrl) return;
  if (tenant && isVbsTabTenant(tenant)) {
    setVbsIconLinks();
    return;
  }

  const iconHref = absoluteUrl(iconUrl);

  document.head
    .querySelectorAll('link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
    .forEach((link) => link.remove());

  [
    { rel: "icon", type: "image/png" },
    { rel: "shortcut icon", type: "image/png" },
    { rel: "apple-touch-icon", type: "image/png" },
  ].forEach(({ rel, type }) => {
    const link = document.createElement("link");
    link.setAttribute("rel", rel);
    link.setAttribute("type", type);
    link.setAttribute("href", iconHref);
    document.head.appendChild(link);
  });
}

function buildManifest(tenant, brandName, description, iconUrl, accent) {
  const shortName = brandName.length > 18 ? brandName.slice(0, 18) : brandName;
  const icons = isVbsTabTenant(tenant)
    ? VBS_PWA_ICONS.map((icon) => ({ ...icon, src: absoluteUrl(icon.src) }))
    : [
        {
          src: absoluteUrl(iconUrl),
          sizes: "any",
          purpose: "any",
        },
        {
          src: absoluteUrl(iconUrl),
          sizes: "any",
          purpose: "maskable",
        },
      ];
  return {
    short_name: shortName,
    name: brandName,
    description,
    icons,
    start_url: "/m",
    scope: "/",
    display: "standalone",
    theme_color: accent,
    background_color: "#FFFFFF",
  };
}

function setManifest(tenant, brandName, description, iconUrl, accent) {
  if (typeof document === "undefined" || typeof Blob === "undefined") return;
  try {
    const previousUrl = window.__storefrontManifestUrl;
    const manifest = buildManifest(
      tenant,
      brandName,
      description,
      iconUrl,
      accent
    );
    const blob = new Blob([JSON.stringify(manifest)], {
      type: "application/manifest+json",
    });
    const url = URL.createObjectURL(blob);
    setLink('link[rel="manifest"]', url);
    window.__storefrontManifestUrl = url;
    if (previousUrl) URL.revokeObjectURL(previousUrl);
  } catch {
    // Keep the static manifest if the browser rejects dynamic manifests.
  }
}

export function applyStorefrontDocumentMetadata(tenant) {
  if (typeof document === "undefined") return;

  const host =
    typeof window !== "undefined" ? cleanText(window.location.hostname) : "";
  const brandName = resolveBrandName(tenant);
  const tagline = cleanText(tenant?.storefrontHeaderTagline);
  const description =
    cleanText(tenant?.storefrontHeroSubtitle) ||
    tagline ||
    `${brandName} storefront`;
  const accent = /^#[0-9a-fA-F]{6}$/.test(
    cleanText(tenant?.storefrontAccentColor)
  )
    ? cleanText(tenant?.storefrontAccentColor)
    : DEFAULT_ACCENT;
  const iconUrl = resolveTabIconUrl(tenant, brandName, accent);
  const imageUrl = resolveAssetUrl(
    tenant?.storefrontHeroImageUrl || tenant?.brandLogoUrl,
    DEFAULT_IMAGE
  );
  const canonicalUrl = currentCanonicalUrl();

  document.title = brandName;
  const tenantKey = resolveTenantKey(tenant);
  const localeRaw =
    tenantKey === "mx"
      ? "es-MX"
      : cleanText(tenant?.defaultLocale) || "en";
  document.documentElement.lang = localeRaw.toLowerCase().startsWith("es") ? "es-MX" : localeRaw;

  setIconLinks(iconUrl, tenant);
  setManifest(tenant, brandName, description, iconUrl, accent);
  setLink('link[rel="canonical"]', canonicalUrl);

  setMetaByName("description", description);
  setMetaByName("theme-color", accent);
  setMetaByName("application-name", brandName);
  setMetaByName("apple-mobile-web-app-title", brandName);

  setMetaByProperty("og:site_name", brandName);
  setMetaByProperty("og:title", brandName);
  setMetaByProperty("og:description", description);
  setMetaByProperty("og:url", canonicalUrl);
  setMetaByProperty("og:image", absoluteUrl(imageUrl));

  setMetaByName("twitter:domain", host);
  setMetaByName("twitter:url", canonicalUrl);
  setMetaByName("twitter:title", brandName);
  setMetaByName("twitter:description", description);
  setMetaByName("twitter:image", absoluteUrl(imageUrl));
}
